import type {
    AListFeeUnit,
    CommissionStatus,
    NamedRow,
    OrderAddress,
    OrderLine,
    OrderLineStatus,
    OrderLineStatusOption,
    OrderLineUpdateInput,
    OrderOccasion,
    OrderRecord,
    OrderStatus,
    OrderUpdateInput,
} from '@/features/orders/api';
import { COPY } from '@/features/orders/copy';
import { moneyText, parseNumber } from '@/features/orders/lineDraft';
import { lineStatusText } from '@/features/orders/orders';
import { ApiError } from '@/lib/api';
import { formatDashedDate, formatMoney, isoToday } from '@/lib/format';

/*
  The order detail page's rules, kept out of its components so they can be tested on their own:
  how the header and the summary card read, what the two drawers start from and send, and what
  each refuses before the server has to.

  Nothing here works out money. Every figure is the one stored on the order, or the server's own,
  as the commission due is, and printed as it stands; the only arithmetic is a percentage beside a
  figure the server already gave, which is how the design prints margin.
*/

const R = COPY.record;

/** The order's tabs, in the strip's order. The address carries the one open, so a reload keeps it. */
export const ORDER_TABS = ['items', 'payments', 'shipping'] as const;
export type OrderTab = (typeof ORDER_TABS)[number];

// ---- the header and the summary card ----

/** "Placed 12-Jun-2026 via Client Appointment · Created by Elana R". */
export function recordSubtitle(record: OrderRecord): string {
    return R.subtitle(
        formatDashedDate(record.orderDate),
        record.saleSource?.name ?? null,
        record.createdBy,
    );
}

/**
 * The currency's own sign, "£", "€", "$", for the Currency fact. The narrow form, because the
 * code stands beside it and "US$" after "USD" says the country twice.
 */
export function currencySymbol(code: string): string {
    try {
        const part = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: code,
            currencyDisplay: 'narrowSymbol',
        })
            .formatToParts(0)
            .find((p) => p.type === 'currency');
        return part?.value ?? code;
    } catch {
        return code;
    }
}

/** "GBP · £ · 1.000", as the prototype's Currency fact reads. */
export function currencyFact(record: Pick<OrderRecord, 'currency' | 'exchangeRate'>): string {
    return `${record.currency} · ${currencySymbol(record.currency)} · ${record.exchangeRate.toFixed(3)}`;
}

export function firstName(name: string): string {
    return name.trim().split(/\s+/)[0] ?? name;
}

/**
 * "Kayla, Scarlett": the people on the sale, the prototype's Sales team. The relationship manager
 * and the lines' assignees, first names, each once. The legacy's own sales team column is empty
 * on every order of the last year, so this is the only honest reading of it.
 */
export function salesTeam(record: Pick<OrderRecord, 'client' | 'assignedTo'>): string | null {
    const names = [record.client.relationshipManager, ...record.assignedTo.map((a) => a.name)]
        .filter((n): n is string => Boolean(n && n.trim()))
        .map(firstName);
    const unique = names.filter((n, i) => names.indexOf(n) === i);
    return unique.length ? unique.join(', ') : null;
}

/** An amount the way the design's summary figures print it: in the order's currency, to the penny. */
export function money(amount: number, currency: string): string {
    return formatMoney(amount, currency);
}

/**
 * The line under the commission badge: what the suppliers owe, or have paid once it is Paid. Empty
 * for an order without commission, which draws no badge to sit under.
 */
export function commissionNote(record: Pick<OrderRecord, 'commission' | 'currency'>): string {
    const commission = record.commission;
    if (!commission) return '';
    if (commission.due === null) return R.money.commissionUnknown;
    const amount = money(commission.due, record.currency);
    return commission.status === 'Paid'
        ? R.money.commissionPaid(amount)
        : R.money.commissionDue(amount);
}

/** Edit order's hint under the commission select: the amount due, when the server could say. */
export function commissionHint(record: Pick<OrderRecord, 'commission' | 'currency'>): string {
    const due = record.commission?.due ?? null;
    return due === null
        ? R.editOrder.commissionHintUnknown
        : R.editOrder.commissionHint(money(due, record.currency));
}

/**
 * "£632.00 · 15%": the stored margin and what share of the stored net it is. No share when there is
 * no net to take it of, rather than a division by nothing.
 */
export function marginText(
    record: Pick<OrderRecord, 'estimatedMargin' | 'net' | 'currency'>,
): string {
    const figure = money(record.estimatedMargin, record.currency);
    if (record.net <= 0) return figure;
    return `${figure} · ${Math.round((record.estimatedMargin / record.net) * 100)}%`;
}

export interface SummaryBand {
    tone: 'destructive' | 'warning' | 'success';
    title: string;
    body: string | null;
}

/**
 * The band across the top of the summary card, or none: the ticket's four states in the
 * prototype's order (renderBanner), and the deleted order, which is ours since the design has
 * none. A cancelled order says so, with its reason, before anything about its money; then what is
 * owed and by when, naming the instalment when the order has more than one; then an order whose
 * money all went back; then one paid in full. With nothing to say there is no band at all.
 */
export function summaryBand(record: OrderRecord): SummaryBand | null {
    const payments = record.payments;
    if (record.deleted) {
        return { tone: 'destructive', title: R.band.deleted, body: R.band.deletedBody };
    }
    if (record.status === 'Cancelled') {
        return { tone: 'destructive', title: R.band.cancelled, body: cancellationReason(record) };
    }
    if (payments.outstanding > 0) {
        const next = payments.next;
        const deposit = payments.deposit;
        return {
            tone: 'warning',
            title: R.band.balance(
                bandMoney(next?.amount ?? payments.outstanding, record.currency),
                next?.dueOn ? formatDashedDate(next.dueOn) : null,
                next && next.of > 1 ? R.band.instalment(next.instalment, next.of) : null,
            ),
            body:
                deposit?.paid === true
                    ? R.band.deposit(
                          bandMoney(deposit.amount, record.currency),
                          deposit.receivedOn ? formatDashedDate(deposit.receivedOn) : null,
                      )
                    : null,
        };
    }
    if (payments.refundedInFull) {
        return { tone: 'destructive', title: R.band.refunded, body: null };
    }
    if (record.payment === 'FullyPaid' && record.net > 0) {
        return {
            tone: 'success',
            title: R.band.paid(bandMoney(record.net, record.currency)),
            body: null,
        };
    }
    return null;
}

/** Whole pounds print without pennies, as the prototype's band does: "£4,210 received". */
function bandMoney(amount: number, currency: string): string {
    return formatMoney(amount, currency, Number.isInteger(amount) ? 0 : 2);
}

/**
 * Why the order was cancelled: the reason given here when this admin cancelled it, and otherwise
 * the reasons the old portal kept on its lines, each once.
 */
export function cancellationReason(record: OrderRecord): string | null {
    if (record.cancellation) return record.cancellation.reason;
    const reasons = record.lines
        .map((line) => line.reason?.trim())
        .filter((r): r is string => Boolean(r));
    const unique = reasons.filter((r, i) => reasons.indexOf(r) === i);
    return unique.length ? unique.join('; ') : null;
}

/** An address on one line, its parts joined the way the prototype prints one. */
export function addressText(address: OrderAddress | null): string | null {
    if (!address || address.lines.length === 0) return null;
    return address.lines.join(', ');
}

/** "Birthday · Self · 02-Nov-2026", as the prototype's Occasion select lists a special date. */
export function occasionText(occasion: OrderOccasion): string {
    return [occasion.occasion, occasion.relation, formatDashedDate(occasion.date)]
        .filter((part): part is string => Boolean(part && part.trim()))
        .join(' · ');
}

/** Initials for the client's avatar: "MD" for Marisa Delacroix. */
export function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase();
}

// ---- the Items tab ----

/** "£151.00 · 28%": the line's margin and its share of what the line sells for. */
export function lineMarginText(line: OrderLine, currency: string): string {
    const figure = money(line.margin, currency);
    const sale = line.unitPrice * line.qty;
    if (sale <= 0) return figure;
    return `${figure} · ${Math.round((line.margin / sale) * 100)}%`;
}

/** The commission fact: "21% (auto)" for a resolved rate, Yes for a flag with no rate that day, None. */
export function lineCommissionText(line: Pick<OrderLine, 'commission' | 'commissionRate'>): string {
    if (!line.commission) return COPY.expansion.none;
    if (line.commissionRate === null) return R.yes;
    return R.items.facts.auto(
        `${line.commissionRate.toLocaleString('en-GB', { maximumFractionDigits: 2 })}%`,
    );
}

/** The discount fact: "10%", or the prototype's "Not available". */
export function lineDiscountText(line: Pick<OrderLine, 'discountPercentage'>): string {
    return line.discountPercentage ? `${line.discountPercentage}%` : R.items.facts.notAvailable;
}

/** A day as the Orders screens write one, or the prototype's en dash for none. */
export function dayOrDash(value: string | null): string {
    return value ? formatDashedDate(value) : R.facts.none;
}

// ---- Edit order ----

/** What Edit order edits: the order's fields, and its three fees while its money is open (ALS-259). */
export interface OrderDraft {
    status: OrderStatus;
    /** An ISO day. */
    orderDate: string;
    saleSourceId: number | null;
    /** Null while the lines are assigned to several people and nobody has chosen one. */
    assignedToId: number | null;
    occasionId: number | null;
    followUpDate: string;
    shippingAddressId: number | null;
    billingAddressId: number | null;
    xeroReference: string;
    externalNote: string;
    remarks: string;
    /** Null on an order without commission, which has no status to edit. */
    commissionStatus: CommissionStatus | null;
    /** Typed, so a field mid-edit is not rewritten under the caret. */
    shippingFee: string;
    shippingTaxId: number | null;
    duties: string;
    dutiesTaxId: number | null;
    aListFeeUnit: AListFeeUnit;
    aListFee: string;
}

export type OrderDraftErrors = Partial<Record<keyof OrderDraft, string>>;

export function orderDraftFrom(record: OrderRecord): OrderDraft {
    return {
        status: record.status,
        orderDate: record.orderDate.slice(0, 10),
        saleSourceId: record.saleSource?.id ?? null,
        // One person, or nobody chosen: the lines disagree, and the drawer says so.
        assignedToId: record.assignedTo.length === 1 ? record.assignedTo[0].id : null,
        occasionId: record.occasion?.id ?? null,
        followUpDate: record.followUpDate ?? '',
        shippingAddressId: record.shippingAddress?.id ?? null,
        billingAddressId: record.billingAddress?.id ?? null,
        xeroReference: record.xeroReference ?? '',
        externalNote: record.externalNote ?? '',
        remarks: record.remarks ?? '',
        commissionStatus: record.commission?.status ?? null,
        shippingFee: moneyText(record.shippingFee),
        shippingTaxId: record.money.shippingTaxId,
        duties: moneyText(record.duties),
        dutiesTaxId: record.money.dutiesTaxId,
        aListFeeUnit: record.money.aListFeeUnit,
        aListFee:
            record.money.aListFeeUnit === 'Percentage'
                ? String(record.money.aListFee)
                : moneyText(record.money.aListFee),
    };
}

/** The products before tax, what a percentage A-List fee is a share of. */
export function productsBeforeTax(record: OrderRecord): number {
    return record.lines.reduce((sum, line) => sum + line.totalBeforeTax, 0);
}

/**
 * The A-List fee written in the other unit, so switching it keeps the fee rather than rereading
 * the figure, as New order's switch does. Worked out from the products here for the field only;
 * what the order is charged is the server's, on save.
 */
export function aListFeeSwitched(
    draft: OrderDraft,
    record: OrderRecord,
    unit: AListFeeUnit,
): string {
    if (unit === draft.aListFeeUnit) return draft.aListFee;
    const value = parseNumber(draft.aListFee) ?? 0;
    const products = productsBeforeTax(record);
    if (unit === 'Percentage') {
        return products > 0 ? String(Math.round((value / products) * 1_000_000) / 10_000) : '0';
    }
    return moneyText(Math.round(products * value) / 100);
}

/** Whether any of the three fees differs from the order's. */
export function feesChanged(draft: OrderDraft, record: OrderRecord): boolean {
    const saved = orderDraftFrom(record);
    return (
        parseNumber(draft.shippingFee) !== parseNumber(saved.shippingFee) ||
        draft.shippingTaxId !== saved.shippingTaxId ||
        parseNumber(draft.duties) !== parseNumber(saved.duties) ||
        draft.dutiesTaxId !== saved.dutiesTaxId ||
        draft.aListFeeUnit !== saved.aListFeeUnit ||
        parseNumber(draft.aListFee) !== parseNumber(saved.aListFee)
    );
}

export function sameOrderDraft(a: OrderDraft, b: OrderDraft): boolean {
    return (Object.keys(a) as (keyof OrderDraft)[]).every((key) => a[key] === b[key]);
}

const blankToNull = (value: string) => (value.trim() === '' ? null : value.trim());

/**
 * The request, from the draft. The assignee goes only when somebody chose one different from the
 * lines' own, because sending it reassigns every line on the order.
 */
export function orderInput(draft: OrderDraft, record: OrderRecord): OrderUpdateInput {
    const saved = orderDraftFrom(record);
    return {
        status: draft.status,
        orderDate: draft.orderDate,
        saleSourceId: draft.saleSourceId ?? 0,
        assignedToId:
            draft.assignedToId !== null && draft.assignedToId !== saved.assignedToId
                ? draft.assignedToId
                : null,
        occasionId: draft.occasionId,
        followUpDate: blankToNull(draft.followUpDate),
        shippingAddressId: draft.shippingAddressId ?? 0,
        billingAddressId: draft.billingAddressId ?? 0,
        xeroReference: blankToNull(draft.xeroReference),
        externalNote: blankToNull(draft.externalNote),
        remarks: blankToNull(draft.remarks),
        revisedOn: record.revisedOn,
        commissionStatus: record.commission ? draft.commissionStatus : null,
        // As stored they change nothing, so the server can tell a fee moved from one sent back.
        fees: {
            shippingFee: parseNumber(draft.shippingFee) ?? 0,
            shippingTaxId: draft.shippingTaxId,
            duties: parseNumber(draft.duties) ?? 0,
            dutiesTaxId: draft.dutiesTaxId,
            aListFeeUnit: draft.aListFeeUnit,
            aListFee: parseNumber(draft.aListFee) ?? 0,
        },
    };
}

/**
 * What the drawer refuses before sending: the same rules the server holds, said at the field.
 * The server is still the judge; this only saves a round trip for the plain cases.
 */
export function validateOrderDraft(
    draft: OrderDraft,
    record: OrderRecord,
    today: string = isoToday(),
): OrderDraftErrors {
    const errors: OrderDraftErrors = {};
    const saved = orderDraftFrom(record);
    // Judged only when it is being changed: a date already on the order must not block a save
    // that leaves it alone, least of all once money is taken and the field is locked.
    if (draft.orderDate === '') errors.orderDate = 'Choose the order date.';
    else if (draft.orderDate !== saved.orderDate && record.moneyTaken)
        errors.orderDate = R.editOrder.orderDateLocked;
    else if (draft.orderDate !== saved.orderDate && draft.orderDate > today)
        errors.orderDate = 'Not a date in the future.';
    // A follow-up is still to do (ALS-498). Judged only when it moves, as the order date is, so an
    // old order's past follow-up does not block a save; clearing it is always allowed.
    if (
        draft.followUpDate !== saved.followUpDate &&
        draft.followUpDate !== '' &&
        draft.followUpDate < today
    )
        errors.followUpDate = "Follow-up date can't be in the past.";
    if (draft.saleSourceId === null) errors.saleSourceId = 'Choose a sale source.';
    if (draft.shippingAddressId === null) errors.shippingAddressId = 'Choose a shipping address.';
    if (draft.billingAddressId === null) errors.billingAddressId = 'Choose a billing address.';
    if (draft.xeroReference.trim().length > 100) errors.xeroReference = 'At most 100 characters.';
    if (draft.remarks.trim().length > 500) errors.remarks = 'At most 500 characters.';
    if (draft.externalNote.trim().length > 4000) errors.externalNote = 'At most 4,000 characters.';
    // The fees are judged only when they changed, as the order date is: a paid order sends them
    // back as stored.
    if (feesChanged(draft, record)) {
        if (record.money.locked !== null) {
            const why =
                record.money.locked === 'MoneyTaken'
                    ? R.editOrder.feeLocked
                    : R.editOrder.feeCancelled;
            if (
                parseNumber(draft.shippingFee) !== parseNumber(saved.shippingFee) ||
                draft.shippingTaxId !== saved.shippingTaxId
            )
                errors.shippingFee = why;
            if (
                parseNumber(draft.duties) !== parseNumber(saved.duties) ||
                draft.dutiesTaxId !== saved.dutiesTaxId
            )
                errors.duties = why;
            if (
                draft.aListFeeUnit !== saved.aListFeeUnit ||
                parseNumber(draft.aListFee) !== parseNumber(saved.aListFee)
            )
                errors.aListFee = why;
        }
        if (!pennies(draft.shippingFee)) errors.shippingFee = MONEY;
        if (!pennies(draft.duties)) errors.duties = MONEY;
        if (draft.aListFeeUnit === 'Amount' && !pennies(draft.aListFee)) errors.aListFee = MONEY;
        if (draft.aListFeeUnit === 'Percentage') {
            const percent = parseNumber(draft.aListFee);
            if (percent === null || percent < 0 || percent > 100)
                errors.aListFee = 'A percentage from 0 to 100.';
        }
    }
    return errors;
}

const MONEY = 'An amount to the penny, no less than zero.';

/** Money as the server takes it: no less than zero and to the penny; empty is nothing. */
function pennies(text: string): boolean {
    if (text.trim() === '') return true;
    const value = parseNumber(text);
    return value !== null && value >= 0 && Math.round(value * 100) === value * 100;
}

/**
 * A 400's field messages, keyed as the form keys its fields. The API names them as its request
 * does, "OrderDate", and the form the same with a lower-case first letter.
 */
export function fieldErrorsFrom<K extends string>(
    error: unknown,
    keys: readonly K[],
): Partial<Record<K, string>> {
    if (!(error instanceof ApiError) || error.status !== 400 || !error.problem.errors) return {};
    const found: Partial<Record<K, string>> = {};
    for (const [key, messages] of Object.entries(error.problem.errors)) {
        const field = (key.charAt(0).toLowerCase() + key.slice(1)) as K;
        if (keys.includes(field) && messages[0]) found[field] = messages[0];
    }
    return found;
}

/**
 * What a drawer's failure slot shows for a failed save: nothing when every refusal landed on a
 * field, the API's own words for one it explained, and "could not save" for anything else.
 */
export function saveFailure<K extends string>(error: unknown, keys: readonly K[]): ApiError | null {
    if (!error) return null;
    if (Object.keys(fieldErrorsFrom(error, keys)).length > 0) return null;
    return error instanceof ApiError ? error : new ApiError(0, {});
}

export const ORDER_DRAFT_KEYS: readonly (keyof OrderDraft)[] = [
    'status',
    'orderDate',
    'saleSourceId',
    'assignedToId',
    'occasionId',
    'followUpDate',
    'shippingAddressId',
    'billingAddressId',
    'xeroReference',
    'externalNote',
    'remarks',
    'commissionStatus',
    'shippingFee',
    'shippingTaxId',
    'duties',
    'dutiesTaxId',
    'aListFee',
];

/** The picker's rows: the options, and the order's own value even when the options no longer offer it. */
export function withCurrent<T extends { id: number }>(rows: readonly T[], current: T | null): T[] {
    if (current === null || rows.some((row) => row.id === current.id)) return [...rows];
    return [current, ...rows];
}

/** "Several people" when the lines disagree; otherwise the one name. */
export function assigneeRows(record: OrderRecord, staff: readonly NamedRow[]): NamedRow[] {
    return record.assignedTo.reduce<NamedRow[]>(
        (rows, person) => withCurrent(rows, person),
        [...staff],
    );
}

// ---- Edit line ----

export interface LineDraft {
    status: OrderLineStatus;
    /** The In wardrobe select, which only counts at a status that puts a piece in the wardrobe. */
    inWardrobe: boolean;
    tracking: string;
    estimatedDelivery: string;
    actualDelivery: string;
    deliveryComment: string;
    followUpDate: string;
    reason: string;
    /** The line's Xero sales account (ALS-386); null for an old portal line whose product names none. */
    accountId: number | null;
}

export type LineDraftErrors = Partial<Record<keyof LineDraft, string>>;

export const LINE_DRAFT_KEYS: readonly (keyof LineDraft)[] = [
    'status',
    'inWardrobe',
    'tracking',
    'estimatedDelivery',
    'actualDelivery',
    'deliveryComment',
    'followUpDate',
    'reason',
    'accountId',
];

/** Whether arriving at this status can put the piece in the client's wardrobe. */
export function allowsWardrobe(
    status: OrderLineStatus,
    options: readonly OrderLineStatusOption[],
): boolean {
    return options.some((option) => option.status === status && option.allowsWardrobe);
}

/**
 * The line as Edit line opens it. In wardrobe reads the line's own flag at a status that uses it,
 * and Yes otherwise, so choosing Delivered offers Yes: 2,870 of the 2,909 lines delivered on last
 * year's orders are in the wardrobe, and the prototype's No would quietly empty the client's.
 */
export function lineDraftFrom(
    line: OrderLine,
    options: readonly OrderLineStatusOption[],
): LineDraft {
    return {
        status: line.status,
        inWardrobe: allowsWardrobe(line.status, options) ? line.showInWardrobe : true,
        tracking: line.tracking ?? '',
        estimatedDelivery: line.estimatedDelivery ?? '',
        actualDelivery: line.actualDelivery ?? '',
        deliveryComment: line.deliveryComment ?? '',
        followUpDate: line.followUpDate ?? '',
        reason: line.reason ?? '',
        accountId: line.accountId,
    };
}

export function sameLineDraft(a: LineDraft, b: LineDraft): boolean {
    return (Object.keys(a) as (keyof LineDraft)[]).every((key) => a[key] === b[key]);
}

/** The statuses that carry a why: the exchange, cancellation and return families, and the retired two. */
const REASONED: ReadonlySet<OrderLineStatus> = new Set<OrderLineStatus>([
    'ExchangeRequested',
    'ExchangeInProgress',
    'ExchangeShipped',
    'ExchangeDelivered',
    'CancellationRequested',
    'CancellationConfirmed',
    'CancellationRefunded',
    'ReturnRequested',
    'RefundInProgress',
    'RefundCompleted',
    'Exchange',
    'Cancelled',
    'Refunded',
]);

export function wantsReason(status: OrderLineStatus): boolean {
    return REASONED.has(status);
}

export function lineInput(
    draft: LineDraft,
    line: OrderLine,
    options: readonly OrderLineStatusOption[],
): OrderLineUpdateInput {
    return {
        // Only when changed. Sent every time, it would put back the status the page read over one
        // the client's app has set since, which the stale check cannot see (ADR 0049).
        status: draft.status !== line.status ? draft.status : null,
        tracking: blankToNull(draft.tracking),
        estimatedDelivery: blankToNull(draft.estimatedDelivery),
        actualDelivery: blankToNull(draft.actualDelivery),
        deliveryComment: blankToNull(draft.deliveryComment),
        followUpDate: blankToNull(draft.followUpDate),
        inWardrobe: allowsWardrobe(draft.status, options) ? draft.inWardrobe : null,
        // Only where it is kept; anywhere else the server drops it, and sending it would say otherwise.
        reason: wantsReason(draft.status) ? blankToNull(draft.reason) : null,
        revisedOn: line.revisedOn,
        // Only when changed, and never once the order is invoiced: its invoice keeps the account.
        ...(draft.accountId !== line.accountId && !line.accountLocked
            ? { accountId: draft.accountId }
            : {}),
    };
}

export function validateLineDraft(
    draft: LineDraft,
    line: Pick<OrderLine, 'actualDelivery' | 'followUpDate'>,
    today: string = isoToday(),
): LineDraftErrors {
    const errors: LineDraftErrors = {};
    // Judged only when it is being changed, as the server judges it: a future date already on the
    // line must not block a save, least of all on a line whose delivery record is locked.
    if (
        draft.actualDelivery !== '' &&
        draft.actualDelivery !== (line.actualDelivery ?? '') &&
        draft.actualDelivery > today
    )
        errors.actualDelivery = 'Not a date in the future: this is when it arrived.';
    // A follow-up is still to do, as the order's is (ALS-532). Judged only when it moves, so a line's
    // past follow-up does not block a save that leaves it alone; clearing it is always allowed.
    if (
        draft.followUpDate !== '' &&
        draft.followUpDate !== (line.followUpDate ?? '') &&
        draft.followUpDate < today
    )
        errors.followUpDate = "Follow-up date can't be in the past.";
    if (draft.tracking.trim().length > 1500) errors.tracking = 'At most 1,500 characters.';
    if (draft.deliveryComment.trim().length > 1500)
        errors.deliveryComment = 'At most 1,500 characters.';
    if (draft.reason.trim().length > 2000) errors.reason = 'At most 2,000 characters.';
    return errors;
}

export interface LineStatusChoice {
    status: OrderLineStatus;
    label: string;
    disabled: boolean;
}

/**
 * What Product status offers: the statuses this order's lines may be given, in the design's flow
 * order, and the line's own status at the top when it is not one of them (a retired status, or
 * Pending confirmation on an order with money on it), so the select can show where the line is
 * without offering it as a choice.
 */
export function lineStatusChoices(
    line: OrderLine,
    options: readonly OrderLineStatusOption[],
): LineStatusChoice[] {
    const choices = options.map((option) => ({
        status: option.status,
        label: lineStatusText(option.status, option.status),
        // One the person's switches do not reach stays in the list, refused, and so does every
        // move out of a status they do not reach (ALS-522).
        disabled: statusRefused(option, [line], options),
    }));
    if (choices.some((choice) => choice.status === line.status)) return choices;
    return [
        {
            status: line.status,
            label: lineStatusText(line.status, line.statusName),
            disabled: true,
        },
        ...choices,
    ];
}

/** The switch a line in this status needs to leave it, when the person does not have it (ALS-522). */
function switchedOffFor(status: OrderLineStatus, options: readonly OrderLineStatusOption[]) {
    return options.find((option) => option.status === status)?.switchedOff ?? null;
}

/**
 * Whether giving these lines this status is refused by the person's switches, as the server judges
 * it: the status needs one they do not have, or a line that would move sits in a status that does,
 * because leaving a cancellation or a return needs its switch too (ALS-522).
 */
export function statusRefused(
    option: OrderLineStatusOption,
    lines: readonly Pick<OrderLine, 'status'>[],
    options: readonly OrderLineStatusOption[],
): boolean {
    if (option.switchedOff) return true;
    return lines.some(
        (line) => line.status !== option.status && switchedOffFor(line.status, options) !== null,
    );
}

/**
 * Why some of Product status's choices are refused, when they are: the person's cancel or returns
 * switch is off (ALS-522). Said under the field, because a disabled choice in a list cannot say it.
 * When a line being edited sits in a status that switch guards, that is said instead, since then
 * it cannot move at all.
 */
export function switchedOffNote(
    options: readonly OrderLineStatusOption[],
    lines: readonly Pick<OrderLine, 'status'>[] = [],
): string | undefined {
    const held = lines.map((line) => switchedOffFor(line.status, options));
    const keepsCancel = held.includes('CancelOrders');
    const keepsReturns = held.includes('ProcessReturns');
    const K = R.editLine.keepsStatus;
    if (keepsCancel && keepsReturns) return K.both;
    if (keepsCancel) return K.cancel;
    if (keepsReturns) return K.returns;

    const cancel = options.some((option) => option.switchedOff === 'CancelOrders');
    const returns = options.some((option) => option.switchedOff === 'ProcessReturns');
    const S = R.editLine.switchedOff;
    if (cancel && returns) return S.both;
    if (cancel) return S.cancel;
    if (returns) return S.returns;
    return undefined;
}

/** Every piece on the order, for Order quantity. */
export function itemCount(record: Pick<OrderRecord, 'lines'>): number {
    return record.lines.reduce((sum, line) => sum + line.qty, 0);
}
